import {
	Component,
	ChangeDetectionStrategy,
	forwardRef,
	input,
	model,
	output,
	ViewEncapsulation,
	ElementRef,
	viewChild,
	computed
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
	selector: 'skl-switch',
	standalone: true,
	template: `
		<label [attr.for]="id()" class="switch">
			{{ label() }}
			<input
				#inputEl
				type="checkbox"
				role="switch"
				[id]="id()"
				[name]="name() || ''"
				[disabled]="disabled()"
				[checked]="isChecked()"
				[indeterminate]="indeterminate()"
				(change)="handleChange($event)"
				(blur)="handleBlur()"
			/>
		</label>
	`,
	styles: `
		:host {
			--focus-outline: var(--primary-color-light);
			--brand-color: var(--primary-color, oklch(0.54 0.25 271.21));

			--thumb-size: 1rem;
			--thumb: light-dark(hsl(0 0% 98%), hsl(0 0% 60%));
			--track-active: light-dark(var(--brand-color), var(--brand-color));
			--track-inactive: light-dark(hsl(0 0% 90%), hsl(0 0% 32%));
			--track-size: calc(var(--thumb-size) * 2);
			--track-padding: 2px;
			--thumb-color: var(--thumb);
			--track-color-inactive: var(--track-inactive);
			--track-color-active: var(--track-active);
			--isLTR: 1;

			align-self: stretch;
			width: 100%;
		}

		:host([disabled]) {
			--_switch-cursor: default;

			pointer-events: none;
			transition: opacity .25s ease;
			opacity: .5;
		}

		:has(:focus-visible) input {
			outline-offset: .1875rem;
			outline: 2px solid var(--focus-outline);
		}

		.switch {
			display: flex;
			align-items: center;
			gap: 2ch;
			justify-content: space-between;
			cursor: var(--_switch-cursor, pointer);
			user-select: none;
			-webkit-tap-highlight-color: transparent;

			& > :where(input) {
				--thumb-position: 0%;
				--thumb-transition-duration: .25s;

				all: unset;
				appearance: none;
				padding: var(--track-padding);
				background: var(--track-color-inactive);
				inline-size: var(--track-size);
				block-size: var(--thumb-size);
				border-radius: var(--track-size);
				pointer-events: none;
				touch-action: pan-y;
				border: none;
				box-sizing: content-box;
				flex-shrink: 0;
				display: grid;
				align-items: center;
				grid: [track] 1fr / [track] 1fr;
				transition: background-color .25s ease;
				border: 1px solid rgb(0 0 0 / 8%);
				outline-offset: -3px;
				transition: outline-offset 145ms ease;

				&::before {
					content: "";
					cursor: var(--_switch-cursor, pointer);
					pointer-events: auto;
					grid-area: track;
					inline-size: var(--thumb-size);
					block-size: var(--thumb-size);
					background: var(--thumb-color);
					border-radius: 50%;
					box-shadow: 0 0 0 1px rgb(0 0 0 / 8%);
					transform: translateX(var(--thumb-position));

					transition:
						transform var(--thumb-transition-duration) ease,
						box-shadow .25s ease;
				}

				&:checked {
					--thumb-position: calc((var(--track-size) - 100%) * var(--isLTR));
					--thumb-color: light-dark(var(--thumb), color-mix(in oklch, var(--brand-color), white 60%));

					background: var(--track-color-active);
				}

				&:indeterminate {
					--thumb-position: calc(calc(calc(var(--track-size) / 2) - calc(var(--thumb-size) / 2)) * var(--isLTR));
				}
			}
		}
	`,
	encapsulation: ViewEncapsulation.ShadowDom,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SwitchComponent),
			multi: true,
		},
	],
	host: {
		'[attr.disabled]': "disabled() ? '' : null",
	}
})
export class SwitchComponent implements ControlValueAccessor {
	static idCounter = 0;

	readonly disabled = model<boolean>(false);
	readonly checked = model<boolean>(false);
	readonly id = input<string>(`skl-switch-${SwitchComponent.idCounter++}`);
	readonly label = input<string>('');
	readonly indeterminate = input<boolean>(false);
	readonly name = input<string | undefined>(undefined);
	readonly value = input<any>(undefined);

	readonly artChange = output<{ value: boolean; checked: boolean; indeterminate: boolean }>();

	readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');

	readonly isChecked = computed(() => {
		const val = this.value();
		if (val !== undefined) {
			return !!val;
		}
		return this.checked();
	});

	protected onChange: (value: boolean) => void = () => { };
	protected onTouched: () => void = () => { };

	writeValue(value: boolean | null | undefined) {
		this.checked.set(!!value);
	}

	registerOnChange(fn: (value: boolean) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled.set(isDisabled);
	}

	protected handleChange(event: Event) {
		if (this.disabled()) {
			return;
		}
		const inputElement = event.target as HTMLInputElement;
		const isChecked = inputElement.checked;

		this.checked.set(isChecked);
		this.onChange(isChecked);

		this.artChange.emit({
			value: isChecked,
			checked: isChecked,
			indeterminate: this.indeterminate(),
		});
	}

	protected handleBlur() {
		this.onTouched();
	}
}
